import { useState, useEffect, ReactNode } from "react";
import "../Insights.scss";
import images from "../../../assets/Icons/Icons";
import BarChart from "../../Charts/BarChart";
import { SectionLoader } from "../../Loader/sectionLoader";
import NoDataScreen from "../../EmptyScreen/EmptyScreen";
import { trackEvent } from "../../../mixpanel";
import { MIXPANEL_EVENTS } from "../../../mixpanel/events";
import useWholeChildCompass from "../../../hooks/useWholeChildCompass";
import useEmergingCharacteristics from "../../../hooks/useEmergingCharacteristics";

const data = {
  totalStudents: 1,
  scores: [
    {
      competency: "RELATIONSHIP_SKILLS",
      avgBaseline: 2.5,
      avgCurrent: 2.5,
    },
    {
      competency: "RESPONSIBLE_DECISION_MAKING",
      avgBaseline: 3.5,
      avgCurrent: 3.5,
    },
    {
      competency: "SELF_AWARENESS",
      avgBaseline: 3,
      avgCurrent: 3,
    },
    {
      competency: "SELF_MANAGEMENT",
      avgBaseline: 2,
      avgCurrent: 2,
    },
    {
      competency: "SOCIAL_AWARENESS",
      avgBaseline: 3,
      avgCurrent: 3,
    },
  ],
};

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9M20.25 20.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
      stroke="#390085"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type ExpandedChart = "sel" | "compass" | "characteristics" | null;

const HowTheyLearning = ({
  Admin,
  name,
  learningData,
  isSchool,
  loading,
  selData,
  userId,
  schoolHash,
  classId,
  isStudent,
}: {
  Admin?: boolean;
  name?: string;
  learningData?: any;
  isSchool?: boolean;
  loading?: boolean;
  selData?: any;
  userId?: string;
  schoolHash?: string;
  classId?: string;
  isStudent?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [expandedChart, setExpandedChart] = useState<ExpandedChart>(null);

  const {
    data: characteristicsData,
    loading: characteristicsLoading,
  } = useEmergingCharacteristics(
    schoolHash || "",
    classId || "",
    userId || ""
  );

  const {
    data: compassData,
    loading: compassLoading,
  } = useWholeChildCompass(
    (schoolHash = schoolHash || ""),
    (classId = classId || ""),
    (userId = userId || ""),
  );
  // const {
  //   data: compassData,
  //   loading: compassLoading,
  //   error: compassError,
  // } = useWholeChildCompass(
  //   (schoolHash =
  //     "f05e283f4b0e4779428a15af26b4cdcb6e374123faa55956a587c81220258732"),
  //   (classId = "kg1"),
  //   (userId = "54e864e8-a021-70be-e10b-476d45e4018f"),
  // );

  // const {
  //   data: compassData,
  //   loading: compassLoading,
  //   error: compassError
  // } = useWholeChildCompass(schoolHash || "", classId || "", userId || "");

  // Transform compass attributes to UI format
  const transformedCompassData =
    compassData?.attributes?.map((attr: any) => ({
      label: attr.name.replace(/_/g, " "), // "HOME_ECOSYSTEM" → "HOME ECOSYSTEM"
      positive: attr.current_score, // Score as percentage
      negative: 100 - attr.current_score, // Inverse for visual balance
    })) || [];

  const renderSelChart = (expanded?: boolean) =>
    selData?.data?.scores?.length > 0 ? (
      <BarChart data={selData?.data} expanded={expanded} />
    ) : (
      <img
        src={images.emptyBar}
        alt="No data"
        style={{
          width: "350px",
          height: "200px",
          margin: "10px auto",
        }}
      />
    );

  const renderCompassChart = () =>
    transformedCompassData && transformedCompassData.length > 0 ? (
      transformedCompassData.map((item: any) => (
        <div key={item.label} className="health-row">
          <span className="label">{item.label}</span>

          <div className="health-bar">
            {item.positive > 0 ? <div className="positive" style={{ width: `${item.positive}%` }}>
                {item.positive}%
            </div>: ""}
            {item.negative > 0 ?<div className="negative" style={{ width: `${item.negative}%` }}>
               {item.negative}%
            </div>: ""}
          </div>
        </div>
      ))
    ) : learningData?.schoolHealth ? (
      learningData?.schoolHealth?.map((item: any) => (
        <div key={item.label} className="health-row">
          <span className="label">{item.label}</span>

           <div className="health-bar">
            {item.positive > 0 ? <div className="positive" style={{ width: `${item.positive}%` }}>
               {item.positive}%
            </div>: ""}
            {item.negative > 0 ? <div className="negative" style={{ width: `${item.negative}%` }}>
               {item.negative}%
            </div>: ""}
          </div>
        </div>
      ))
    ) : (
      <NoDataScreen />
    );

  const renderCharacteristics = () =>
    characteristicsLoading ? (
      <SectionLoader />
    ) : characteristicsData &&
      (characteristicsData.strengths.length > 0 ||
        characteristicsData.weaknesses.length > 0) ? (
      <>
        <div className="emerging-legend">
          <span className="emerging-legend-item">
            <span className="emerging-dot strong" />
            Strong traits
          </span>
          <span className="emerging-legend-item">
            <span className="emerging-dot developing" />
            Developing traits
          </span>
        </div>
        <div className="emerging-traits-section">
          <div className="emerging-traits">
            {characteristicsData.strengths.map((trait) => (
              <span key={trait} className="trait-pill strong">
                {trait}
              </span>
            ))}
          </div>
          <div className="emerging-traits">
            {characteristicsData.weaknesses.map((trait) => (
              <span key={trait} className="trait-pill developing">
                {trait}
              </span>
            ))}
          </div>
        </div>
      </>
    ) : (
      <NoDataScreen />
    );

  const expandedChartMeta: Record<
    Exclude<ExpandedChart, null>,
    { title: string; render: (expanded?: boolean) => ReactNode }
  > = {
    sel: { title: "Social Emotional profile", render: renderSelChart },
    compass: { title: "Whole Child Compass", render: renderCompassChart },
    characteristics: {
      title: "Emerging Characteristics",
      render: renderCharacteristics,
    },
  };

  const handleExpandChart = (chart: Exclude<ExpandedChart, null>) => {
    trackEvent(MIXPANEL_EVENTS.CHART_EXPANDED, {
      chart: expandedChartMeta[chart].title,
      view: isSchool ? "School" : "Student",
    });
    setExpandedChart(chart);
  };

  const toggleOpen = () => {
    const willBeOpen = !open;
    if (willBeOpen) {
      trackEvent("SECTION_EXPANDED_TEACHER", {
        section: "What Are We Learning About Them",
        view: isSchool ? "School" : "Student",
      });
    }
    setOpen(willBeOpen);
  };

  return (
    <section className="insight-section">
      <header onClick={toggleOpen} className="section-header">
        <h3>WHAT ARE WE LEARNING ABOUT THEM?</h3>
        <img
          src={open ? images.upIocn : images.downIcon}
          width={44}
          height={44}
        />
      </header>

      {open &&
        (loading ? (
          <SectionLoader />
        ) : (
          <div className="grid three">
            {/* Average time */}

            {isSchool && (
              <div className="cards expandable-card">
                <h5>Social Emotional profile</h5>

                {renderSelChart()}

                <button
                  type="button"
                  className="expand-chart-btn"
                  aria-label="Expand chart"
                  onClick={() => handleExpandChart("sel")}
                >
                  <ExpandIcon />
                </button>
              </div>
            )}

            {/* School Health / Whole Child Compass */}

            {isStudent && (
              <div className="cards expandable-card">
                <h5>Whole Child Compass</h5>

                {renderCompassChart()}

                <button
                  type="button"
                  className="expand-chart-btn"
                  aria-label="Expand chart"
                  onClick={() => handleExpandChart("compass")}
                >
                  <ExpandIcon />
                </button>
              </div>
            )}

            {/* Emerging Characteristics */}
            {isStudent && (
              <div className="cards expandable-card">
                <div className="emerging-header">
                  <h5>Emerging Characteristics</h5>
                </div>

                {renderCharacteristics()}

                <button
                  type="button"
                  className="expand-chart-btn"
                  aria-label="Expand chart"
                  onClick={() => handleExpandChart("characteristics")}
                >
                  <ExpandIcon />
                </button>
              </div>
            )}
          </div>
        ))}

      {expandedChart && (
        <div
          className="chart-modal-overlay"
          onClick={() => setExpandedChart(null)}
        >
          <div
            className="chart-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="chart-modal-close"
              aria-label="Close"
              onClick={() => setExpandedChart(null)}
            >
              <img src={images.closeIcon} width={16} height={16} alt="Close" />
            </button>

            <h5>{expandedChartMeta[expandedChart].title}</h5>

            <div className="chart-modal-body">
              {expandedChartMeta[expandedChart].render(true)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HowTheyLearning;
